import { useState, useEffect, useRef } from "react";

// Stale-while-revalidate: serve localStorage cache immediately, refresh in background.
// fetcher: () => Promise<data>
// Returns { data, loading, refreshing, refresh }
//   loading    — true only on the very first load (no cached data yet)
//   refreshing — true when a background refresh is in flight (stale data is showing)
export function useStaleData(key, fetcher) {
  const [data, setData]           = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading]       = useState(data === null);
  const [refreshing, setRefreshing] = useState(false);
  const fetcherRef                  = useRef(fetcher);
  fetcherRef.current                = fetcher;

  const run = (isBackground) => {
    if (isBackground) setRefreshing(true);
    else setLoading(true);

    return fetcherRef.current()
      .then(fresh => {
        setData(fresh);
        try { localStorage.setItem(key, JSON.stringify(fresh)); } catch {}
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    run(data !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { data, loading, refreshing, refresh: () => run(true) };
}
